import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Content, Group, PublicUserProfile, SearchResultsObject } from 'api';
import { SearchResultTypes } from 'types/app';
import { EmptyState, Layouts, Loaders } from 'components';
import { getSearchResultsType } from 'helpers';

import { ContentResult, GroupResult, Loader, ProfileResult } from '../components';
import { useSearch } from '../hooks';

const useStyles = makeStyles(theme => ({
  '@global body': {
    backgroundColor: theme.palette.common.white,
  },
}));

const SEARCH_LIMIT = 24;

const SearchResults: FC = () => {
  useStyles();

  const { t } = useTranslation();

  const query = new URLSearchParams(location.search);
  const term = query.get('q');

  const { results, count, isEmpty, isExhausted, isInitialLoad, sentinelRef } = useSearch({
    q: term,
    results_from: 0,
    size: SEARCH_LIMIT,
  });

  function renderResult(result: SearchResultsObject) {
    const type = getSearchResultsType(result);

    if (type === SearchResultTypes.Content) {
      const content = result as Content;
      return (
        <Grid item xs key={content.content_id}>
          <ContentResult content={content} />
        </Grid>
      );
    }

    if (type === SearchResultTypes.Group) {
      const group = result as Group;
      return (
        <Grid item xs key={group.id}>
          <GroupResult group={group} />
        </Grid>
      );
    }

    if (type === SearchResultTypes.Profile) {
      const profile = result as PublicUserProfile;
      return (
        <Grid item xs key={profile.id}>
          <ProfileResult profile={profile} />
        </Grid>
      );
    }

    return null;
  }

  function renderMain() {
    if (isInitialLoad) {
      return <Loader />;
    }

    if (isEmpty) {
      return <EmptyState name="search" />;
    }

    return (
      <Fragment>
        <Grid container direction="column" spacing={4}>
          {results.map(renderResult)}
        </Grid>
        {!isExhausted && (
          <div ref={sentinelRef}>
            <Loaders.Standard />
          </div>
        )}
      </Fragment>
    );
  }

  return (
    <Layouts.App1Column
      navigation
      desktopSize={7}
      main={
        <Layouts.AppMain1Column
          dividerColor="red"
          header={
            <Grid container alignItems="center" justify="space-between">
              <Typography color="inherit" variant="h4">
                {t('search:searchTerm', { term })}
              </Typography>
              {!!count && (
                <Typography color="inherit" variant="subtitle2">
                  {t('components:xResult', { count })}
                </Typography>
              )}
            </Grid>
          }
          main={renderMain()}
        />
      }
    />
  );
};

export default SearchResults;
